import { type ReactElement, type ReactNode, useLayoutEffect, useState } from 'react';
import { Router } from 'react-router-dom';
import type { Action, BrowserHistory, Location, MemoryHistory } from 'history';

/**
 * react-router-dom v7 removed the `<Router history>` API (and the helper
 * `unstable_HistoryRouter`). This wrapper restores the external-history pattern by
 * feeding our `sharedHistory` instance into the low-level `<Router>` via `navigator`,
 * keeping `routingHelpers`' `sharedHistory.push(...)` and the listen() side-effects intact.
 *
 * Implementation mirrors the historical `unstable_HistoryRouter`.
 */
interface IHistoryRouterProps {
  history: BrowserHistory | MemoryHistory;
  children: ReactNode;
}

interface IHistoryRouterState {
  action: Action;
  location: Location;
}

const HistoryRouter = ({ history, children }: IHistoryRouterProps): ReactElement => {
  const [state, setState] = useState<IHistoryRouterState>({
    action: history.action,
    location: history.location,
  });

  useLayoutEffect((): (() => void) => history.listen(setState), [history]);

  return (
    <Router
      location={state.location}
      navigationType={state.action}
      navigator={history}
    >
      {children}
    </Router>
  );
};

export default HistoryRouter;
